import * as yup from 'yup';
import { isRuntimeTriggerType } from 'store/triggerType';

// const latLongRegex = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/;

export const loginSchema = yup.object().shape({
  email: yup.string().required('Email is required').email('Email invalid'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
});
export const changePasswordSchema = yup.object().shape({
  currentPassword: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  newPassword: yup
    .string()
    .notOneOf(
      [yup.ref('currentPassword')],
      'The new password cannot be the same as the current password.'
    )
    .required('New Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmNewPassword: yup
    .string()
    .oneOf([yup.ref('newPassword')], 'Passwords do not match. Please retype both fields.')
    .required('Confirm New Password is required')
});
export const forgotPasswordSchema = yup.object().shape({
  email: yup.string().required('Email is required').email('Email invalid')
});
export const resetPasswordSchema = yup.object().shape({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required')
});

export const formClient = yup.object().shape({
  clientName: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'Passwords must match')
    .required('Confirm password is required')
});

export const createClientYuq = yup.object().shape({
  clientName: yup
    .string()
    .required('Client name is required')
    .min(3, 'Client name must be at least 3 characters'),
  clientEmail: yup.string().required('Email is required').email('Email invalid'),
  contactPerson: yup
    .string()
    .required('Contact person is required')
    .min(3, 'Contact person must be at least 3 characters')
});

export const createLocationYuq = yup.object().shape({
  names: yup
    .array()
    .required('Field required')
    .min(1, 'At least one name is required')
    .of(
      yup.object().shape({
        value: yup.string().required('Field required')
      })
    )
});

export const uploadFloorPlan = yup.object().shape({
  names: yup
    .array()
    .required('Field required')
    .min(1, 'At least one name is required')
    .of(
      yup.object().shape({
        value: yup.string().required('Field required'),

        floorPlan: yup
          .mixed<File>()
          .required('Floor plan is required')
          .test(
            'file-type',
            'Only PNG and PDF files are allowed',
            (value) => !!value && ['image/png', 'application/pdf'].includes(value.type)
          )
      })
    )
});

export const updateLocationYup = yup.object({
  value: yup.string().required('Field required'),

  floorPlan: yup
    .mixed<File>()
    .nullable()
    .notRequired()
    .test('file-type', 'Only PNG and PDF files are allowed', (value) => {
      if (!value) return true;
      return value instanceof File && ['image/png', 'application/pdf'].includes(value.type);
    })
});

export const FormOptionPropsSchema = yup.object().shape({
  options: yup
    .array()
    .required('Field required')
    .min(1, 'At least one name is required')
    .of(
      yup.object().shape({
        value: yup.string().required('This field is required'),
        name: yup.string().required('This field is required')
      })
    ),
  groupName: yup.string().required(),
  id: yup.number().required(),
  type: yup.array().required('Field required').min(1, 'At least one select type is required'),
  required: yup.boolean().required()
});

export const noteImageSvgYuq = yup.object().shape({
  idNote: yup.string().required('This field is required')
});

export const FormAddUserSchema = yup.object().shape({
  name: yup.string().required('User name is required'),
  email: yup.string().required('User email is required').email('Invalid email format'),
  phone: yup
    .string()
    .optional()
    .test('phone-e164', 'Phone must be in E.164 format (e.g. +15551234567)', (value) => {
      if (!value) return true;
      return /^\+[1-9]\d{7,14}$/.test(value.trim());
    }),
  role: yup.string().required('Credentials is required'),
  file: yup.mixed().optional()
});

export const FormAddDocumentSchema = yup.object().shape({
  name: yup.string().required('User name is required'),
  clientIds: yup.array().of(yup.string().required()).optional(),
  file: yup
    .mixed()
    .required('File is required')
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    .test('fileSize', 'File size must be less than 500MB', (value: any) => {
      if (!value) {
        return true;
      }
      return value.size <= 500 * 1024 * 1024;
    })
});

export const AssetModelFormSchema = yup.object().shape({
  name: yup.string().required('This field is required'),
  manufacture: yup.string().required('This field is required'),

  otherManufacture: yup.string().when(['manufacture'], {
    is: (val: string) => val === 'other',
    then: (schema) => schema.required('This field is required')
  }),

  type: yup.string().required('This field is required'),
  oemAttribute: yup
    .array()
    .optional()
    .nullable()
    .of(
      yup.object().shape({
        value: yup.string().required('This field is required'),
        label: yup.string().required('This field is required'),
        isDropdown: yup.boolean().optional(),
        dropdownId: yup.number().optional().nullable(),
        dropdownValue: yup.number().optional().nullable()
      })
    ),
  maintenanceEnabled: yup.boolean().optional(),
  maintenanceConfigs: yup.array().when('maintenanceEnabled', {
    is: true,
    then: (schema) =>
      schema
        .required()
        .min(1, 'At least one maintenance schedule is required')
        .of(
          yup.object().shape({
            maintenanceTriggerTypeId: yup
              .string()
              .required('Trigger type is required')
              .trim()
              .min(1, 'Trigger type is required'),
            // Runtime is looked up by code via the trigger type store (not a
            // sibling form field), since maintenanceTriggerTypeId is just an id.
            triggerSubTypeId: yup
              .string()
              .nullable()
              .test('runtime-sub-type-required', 'Sub type is required', function (value) {
                if (!isRuntimeTriggerType(this.parent.maintenanceTriggerTypeId)) return true;
                return !!value && String(value).trim().length > 0;
              }),
            // Always read-only in the UI, auto-set from the selected trigger
            // type's own calculationType — still required so a broken
            // auto-fill fails loudly instead of silently submitting blank.
            calculationType: yup.string().required('Calculation type is required'),
            // Required unless Runtime, where it's forced to null (Runtime's
            // unit comes from the sub type's defaultUnit instead).
            unitId: yup
              .string()
              .nullable()
              .test('unit-required-unless-runtime', 'Unit is required', function (value) {
                if (isRuntimeTriggerType(this.parent.maintenanceTriggerTypeId)) return true;
                return !!value && String(value).trim().length > 0;
              }),
            frequencyType: yup
              .string()
              .oneOf(['INTERVAL', 'RANGE'])
              .required('Frequency type is required'),
            maintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'INTERVAL',
                then: (s) => s.required('Maintenance frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            minMaintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'RANGE',
                then: (s) => s.required('Min frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            maxMaintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'RANGE',
                then: (s) => s.required('Max frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            notificationWindow: yup
              .number()
              .typeError('Must be a number')
              .required('Notification window is required')
              .min(1, 'Must be at least 1'),
            counterSource: yup
              .string()
              .required('Counter source is required')
              .trim()
              .min(1, 'Source type is required')
          })
        ),
    otherwise: (schema) => schema.notRequired()
  })
});

export const TriggerTypeFormSchema = yup.object().shape({
  name: yup.string().required('This field is required'),
  // Always required at the schema level — for the reserved Runtime trigger
  // type the field is hidden and defaulted to 'COUNTER_BASED' rather than
  // exempted, so this stays a plain required check.
  calculationType: yup
    .string()
    .oneOf(['COUNTER_BASED', 'DATE_BASED'], 'Calculation type is required')
    .required('Calculation type is required')
});

export const AssetTypesFormSchema = yup.object().shape({
  name: yup.string().required('This field is required'),
  fields: yup
    .array()
    .of(
      yup.object().shape({
        fieldId: yup.string().optional(),
        unit: yup.string().required('This field is required'),
        category: yup.string().required('This field is required'),
        name: yup.string().required('This field is required'),
        isDelete: yup.boolean().optional()
      })
    )
    .test('unique-names', 'Field names must be unique', (fields) => {
      if (!fields) return true;
      const nameCounts = fields.reduce(
        (acc, field, index) => {
          if (!field.isDelete) {
            acc[field.name] = acc[field.name] ? [...acc[field.name], index] : [index];
          }
          return acc;
        },
        {} as Record<string, number[]>
      );

      return Object.values(nameCounts).every((indexes) => indexes.length === 1);
    })
});
export const FormAddAppendixSchema = yup.object().shape({
  isSelectAll: yup.boolean().optional()
});
export const FormDetailReportSchema = yup.object().shape({
  equipmentId: yup.string().required('Equipment ID is required'),
  name: yup.string().required('Name is required')
});
export const FormCreateReportSchema = yup.object().shape({
  name: yup.string().required('Name is required')
});
export const FormGenReportSchema = yup.object().shape({
  select: yup.string().optional(),
  assets: yup.array().optional(),
  issues: yup.array().optional(),
  roomLevelIssueIds: yup.array().optional(),
  title: yup.string().required('This field is required'),
  showAppendixFile: yup.boolean().optional()
});
export const FormRoomLevelIssuesSchema = yup.object().shape({
  type: yup.string().required('This field is required'),
  typeOther: yup.string().when('type', {
    is: (val: string) => val === 'OTHER',
    then: (schema) => schema.required('Type Other is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  description: yup.string().required('This field is required'),
  actionBy: yup.string().nullable(),
  actionByDate: yup.string().nullable(),
  validated: yup.string().required('This field is required'),
  status: yup.string().required('This field is required')
});
export const FormGenReportInReportPageSchema = yup.object().shape({
  title: yup.string().required('This field is required')
});
export const FormSendMailSchema = yup.object().shape({
  email: yup
    .array()
    .of(yup.string().email('Invalid email').required('Email is required'))
    .min(1, 'At least one email is required')
    .required('Email is required')
});
export const ContentExtraTableValidationSchema = yup.object().shape({
  fields: yup.array().of(
    yup.object().shape({
      fieldId: yup.number().required('This field is required'),
      value: yup.string()
    })
  )
});

export const EditAssetFormSchema = yup.object().shape({
  assetName: yup.string().required('This field is required'),
  validated: yup.string().required('This field is required'),
  // criticality: yup.string().required('This field is required'),
  criticalityKey: yup.string().required('This field is required'),
  model: yup.string().required('This field is required'),
  modelName: yup.string().when('model', {
    is: (val: string) => val === 'OTHER',
    then: (schema) => schema.required('Other model name is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  manufacturerId: yup.string().when('model', {
    is: (val: string) => val === 'OTHER',
    then: (schema) => schema.required('Manufacturer is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  manufacturer: yup.string().when('manufacturerId', {
    is: (val: string) => val === 'OTHER',
    then: (schema) => schema.required('Manufacturer is required'),
    otherwise: (schema) => schema.notRequired()
  }),
  maintenanceEnabled: yup.boolean().optional(), // ← add
  assetDevices: yup.array().when('maintenanceEnabled', {
    // ← add
    is: true,
    then: (s) =>
      s
        .of(
          yup.object({
            frequencyType: yup
              .string()
              .oneOf(['INTERVAL', 'RANGE'])
              .required('Frequency type is required'),
            maintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'INTERVAL',
                then: (s) => s.required('Maintenance frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            minMaintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'RANGE',
                then: (s) => s.required('Min frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            maxMaintenanceFrequency: yup
              .number()
              .nullable()
              .when('frequencyType', {
                is: 'RANGE',
                then: (s) => s.required('Max frequency is required'),
                otherwise: (s) => s.nullable()
              }),
            notificationWindow: yup.number().nullable().optional(),
            currentReadingValue: yup.number().nullable().optional(),
            iotDeviceId: yup.string().nullable().optional(),
            iotDeviceName: yup.string().nullable().optional()
          })
        )
        .optional(),
    otherwise: (s) => s.optional()
  })
});

export const FormSingleFieldPropsSchema = yup.object().shape({
  name: yup.string().required(),
  entries: yup.array().required('Field required').min(1, 'At least one select type is required'),
  required: yup.boolean().required(),
  type: yup.string().required()
});

export const NotificationSettingsSchema = yup.object().shape({
  defaultNotificationWindow: yup
    .number()
    .typeError('Must be a number')
    .required('Notification window is required')
    .min(1, 'Must be at least 1'),

  emailRecipients: yup
    .array()
    .of(yup.string().email('Invalid email address').required())
    .min(1, 'At least one email recipient is required')
    .required('Email recipients are required'),

  dailyReportSchedule: yup.object().shape({
    time: yup.string().required('Daily report time is required'),
    enabled: yup.boolean().required()
  }),

  weeklyReportSchedule: yup.object().shape({
    time: yup.string().required('Weekly report time is required'),
    enabled: yup.boolean().required(),
    dayOfWeek: yup.string().required('Day of week is required')
  }),

  reminderFrequency: yup.object().shape({
    value: yup
      .number()
      .typeError('Must be a number')
      .required('Reminder frequency value is required')
      .min(1, 'Must be at least 1'),
    unit: yup.string().oneOf(['HOURS', 'DAYS']).required('Unit is required')
  })
});
